import {
  Box,
  VStack,
  Button,
  HStack,
  Icon,
  IconButton,
  Link as ChakraLink,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { FileUploadContextDirect } from '@duke-hq/svc-files-ui/FileUploadContextDirect';
import { FileUploadDropzone } from '@duke-hq/svc-files-ui/FileUploadDropzone';
import { FileUploadList } from '@duke-hq/svc-files-ui/FileUploadList';
import { FileUploadTrigger } from '@duke-hq/svc-files-ui/FileUploadTrigger';
import { ACCEPTABLE_FILES } from '@duke-hq/svc-files-lib/schemas';
import type { StudentType } from './StudentDetailPage';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { testIds } from './StudentDocuments/testIds';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Download, FileText, MoreHorizontal, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { djs } from '@duke-hq/djs';
import { Link as RouterLink } from 'react-router-dom';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { formatBytes } from '../components/util/formatBytes';

const MAX_DOCUMENTS = 20;

type StudentDocumentsProps = {
  student: StudentType;
};

export function StudentDocuments({ student }: StudentDocumentsProps) {
  const toast = useToast();
  const utils = trpcAdmin.useUtils();
  const deleteDialog = useDisclosure();
  const [fileToDelete, setFileToDelete] = useState<{ drn: string; filename: string } | null>(null);
  const { currentPage, pageSize, setPage } = usePagination();

  const { data } = trpcAdmin.students.documentFiles.useQuery({
    studentDrn: student.drn,
    pagination: { page: currentPage, limit: pageSize },
  });

  const deleteDocument = trpcAdmin.students.deleteDocument.useMutation({
    onSuccess: () => {
      utils.students.documentFiles.invalidate();
      toast({
        title: t`Document deleted`,
        status: 'success',
      });
    },
    onError: (error) => {
      toast({
        title: t`Failed to delete document`,
        description: error.message,
        status: 'error',
      });
    },
  });

  const getDownloadUrl = trpcAdmin.files.getDownloadUrl.useMutation();

  const handleDownload = async (fileDrn: string) => {
    try {
      const { url } = await getDownloadUrl.mutateAsync({ fileDrn });
      window.open(url, '_blank');
    } catch (error) {
      toast({
        title: t`Failed to download file`,
        description: error instanceof Error ? error.message : t`Unknown error`,
        status: 'error',
      });
    }
  };

  const handleOpenDelete = (fileDrn: string, filename: string) => {
    setFileToDelete({ drn: fileDrn, filename });
    deleteDialog.onOpen();
  };

  const handleConfirmDelete = async () => {
    if (!fileToDelete) {
      return;
    }
    await deleteDocument.mutateAsync({
      studentDrn: student.drn,
      fileDrn: fileToDelete.drn,
    });
    deleteDialog.onClose();
    setFileToDelete(null);
  };

  const existingFiles = data?.items ?? [];

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch" maxW="4xl">
        {existingFiles.length > 0 && (
          <VStack align="stretch" spacing={2}>
            {existingFiles.map((file) => (
              <HStack
                key={file.id}
                w="full"
                borderRadius="8px"
                borderColor="border.emphasis"
                borderWidth="1px"
                bgColor="bg.subtle"
                padding="16px"
                gap="16px"
                justifyContent="space-between"
              >
                <HStack spacing="3" alignItems="center" minW={0}>
                  <Box flexShrink={0} width="30px" color="fg.muted">
                    <Icon as={FileText} boxSize="30px" />
                  </Box>
                  <VStack spacing={0} align="start" minW={0} flexShrink={1}>
                    <HStack spacing="2" minW={0}>
                      <Text isTruncated flexShrink={1}>
                        {file.filename}
                      </Text>
                      <Text flexShrink={0} color="fg.muted" fontSize="sm">
                        {formatBytes(file.sizeInBytes)}
                      </Text>
                    </HStack>
                    <Text color="fg.subtle" fontSize="xs">
                      {file.uploadedBy != null ? (
                        <Trans>
                          Uploaded by{' '}
                          <ChakraLink
                            as={RouterLink}
                            to={`/users/${file.uploadedBy.id}/details`}
                            color="fg.subtle"
                            textDecoration="underline"
                          >
                            {file.uploadedBy.displayName ?? file.uploadedBy.id}
                          </ChakraLink>{' '}
                          on {djs(file.createdAt).format('LLL')}
                        </Trans>
                      ) : (
                        <Trans>Uploaded {djs(file.createdAt).format('LLL')}</Trans>
                      )}
                    </Text>
                  </VStack>
                </HStack>
                <HStack>
                  <IconButton
                    aria-label={t`Download`}
                    icon={<Download size="20px" />}
                    variant="ghost"
                    onClick={() => handleDownload(file.drn)}
                  />
                  <Menu>
                    <MenuButton
                      as={IconButton}
                      aria-label={t`Document options`}
                      icon={<Icon as={MoreHorizontal} boxSize={5} />}
                      variant="ghost"
                    />
                    <MenuList>
                      <MenuItem
                        icon={<Trash2 size={14} />}
                        color="red.500"
                        onClick={() => handleOpenDelete(file.drn, file.filename)}
                      >
                        <Trans>Delete</Trans>
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </HStack>
              </HStack>
            ))}
          </VStack>
        )}

        {data?.pagination != null && data.pagination.pageCount > 1 ? (
          <Pagination pagination={data.pagination} onPageChange={setPage} />
        ) : null}

        <FileUploadContextDirect
          quantity={{ max: MAX_DOCUMENTS }}
          accept={ACCEPTABLE_FILES}
          attachedToId={student.id}
          attachedToKind="STUDENT_DOCUMENT"
          onFileComplete={() => {
            utils.students.documentFiles.invalidate();
          }}
        >
          <FileUploadList hideCompletedFiles />
          <FileUploadDropzone />
          <FileUploadTrigger>
            <Button colorScheme="blue" data-testid={testIds.uploadButton}>
              <Trans>Upload Documents</Trans>
            </Button>
          </FileUploadTrigger>
        </FileUploadContextDirect>
      </VStack>

      <ConfirmationDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.onClose}
        title={<Trans>Delete Document</Trans>}
        body={
          <VStack align="start" spacing={2}>
            <Text>
              <Trans>Are you sure you want to delete &quot;{fileToDelete?.filename}&quot;?</Trans>
            </Text>
            <Text>
              <Trans>This action cannot be undone.</Trans>
            </Text>
          </VStack>
        }
        onConfirm={handleConfirmDelete}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
        isLoading={deleteDocument.isPending}
      />
    </Box>
  );
}
